import {
  DataSetDto,
  DataSetFilterTemplateDto,
  EnumerationOptionDto,
  EnumerationValueListDto,
  type EvaluationParameterDto,
  instanceOfEnumerationOptionDto,
  instanceOfEnumerationValueListDto,
  type MedicalSystemCatalogDto,
  NumericDataSetFilterDto,
  type QaEquipmentCatalogDto,
  TextValueListDto,
} from '@/api/generator';
import { InternalDataTableHeader } from '@/components/OverviewTable/types.ts';

export type TextValueListDtoWithId = TextValueListDto & { id: string };

export type WithDisplayName<T> = T & { displayName?: string | null };
type WithDisplayValues<T> = T & { displayValues?: string[] | null };

export type EnumerationOptionDtoWithDisplayName = WithDisplayName<EnumerationOptionDto>;

export type EnumerationValueListDtoWithDisplayName = {
  $type?: 'Ptw.Ptw100.AppZero.Application.Shared.DataTypeManagement.Dto.EnumerationDto.EnumerationValueListDto, Ptw.Ptw100.AppZero.Application.Shared';
} & WithDisplayName<
  Omit<EnumerationValueListDto, 'options' | 'selectedOptions'> & {
    options?: Array<EnumerationOptionDtoWithDisplayName> | null;
    selectedOptions?: Array<EnumerationOptionDtoWithDisplayName> | null;
  }
>;

export type NumericDataSetFilterDtoWithDisplayName = {
  $type?: 'Ptw.Ptw100.AppZero.Application.Shared.Results.Dto.NumericDataSetFilterDto, Ptw.Ptw100.AppZero.Application.Shared';
} & WithDisplayName<WithDisplayValues<NumericDataSetFilterDto>>;

export type TextValueListDtoWithDisplayValues = {
  $type?: 'Ptw.Ptw100.AppZero.Application.Shared.TaskItemComponent.Dto.SystemSetupParameters.TextValueListDto, Ptw.Ptw100.AppZero.Application.Shared';
} & WithDisplayName<WithDisplayValues<TextValueListDtoWithId>>;

export function hasDisplayName<T>(obj: object): obj is WithDisplayName<T> {
  return obj !== null && typeof obj === 'object' && 'displayName' in obj;
}
export function hasDisplayValues<T>(obj: object): obj is WithDisplayValues<T> {
  return obj !== null && typeof obj === 'object' && 'displayValues' in obj;
}

export function instanceOfEnumerationValueListDtoWithDisplayName(
  value: object,
): value is EnumerationValueListDtoWithDisplayName {
  return instanceOfEnumerationValueListDto(value) && hasDisplayName(value);
}

export function instanceOfEnumerationOptionDtoWithDisplayName(
  value: object,
): value is EnumerationOptionDtoWithDisplayName {
  return instanceOfEnumerationOptionDto(value) && hasDisplayName(value);
}

export type ISetupParameterFilterDisplay =
  | EnumerationValueListDtoWithDisplayName
  | NumericDataSetFilterDtoWithDisplayName
  | TextValueListDtoWithDisplayValues;

export type DataSetFilterTemplateDtoDisplay = DataSetFilterTemplateDto & {
  availableSupportSystems: Array<WithDisplayName<QaEquipmentCatalogDto>>;
  availableTargetSystems: Array<WithDisplayName<MedicalSystemCatalogDto>>;
  targetSystemSetupFilters: Array<ISetupParameterFilterDisplay>;
  supportSystemSetupFilters: Array<ISetupParameterFilterDisplay>;
  evaluationParameterFilters: Array<WithDisplayName<EvaluationParameterDto>>;
};

// Component types
export type DataSetSelectionProps = {
  totalDataSetCount: number;
  maximumDataSetCount?: number;
  dataSets: DataSetDto[] | null;
  selectedDataSets: string[];
  loading?: boolean;
  showDifferences?: boolean;
  showAsLegend?: boolean;
};

export type DataSetSelectionEmits = {
  'update:selectedDataSets': [dataSets: string[]];
};

export type RowData = {
  headerKey: string;
  headerTitle: string;
  isIdenticalOverAllSetupValues: boolean | null;
  parameterNames: string[];
  iuid?: string | null;
  isSeparator?: boolean;
  [key: string]: any; // This allows any additional properties
};

export function isDataSetInternalDataTableHeader(
  column: object,
): column is InternalDataTableHeader & { dataSet: DataSetDto } {
  return 'dataSet' in column;
}
